import {Injectable} from '@angular/core';
import {rangeOfPeriod} from '@vat/engine/month';
import {ENGINE_VERSION} from '@vat/engine/types';
import {NahausRemindersService} from 'app/modules/admin/identity/services/nahausReminders.service';
import {TasksService} from 'app/modules/admin/tasks/services/tasks.service';
import {firstValueFrom} from 'rxjs';
import {VatReturn} from '../export/vat-export.types';
import {buildDeadlineReminderPayload, buildDeadlineTaskPayload, VatDeadlineLeadDays, VatDeadlineRecipient} from './vat-deadline-tasks.logic';
import {buildDraftVatReturn, buildVatReturnID, normalizeVatReturnDates} from './vat-return.factory';
import {VatReturnsService} from './vat-returns.service';

export interface VatDeadlinePeriod {
    year: number;
    periodKey: string;
    dueDate: Date;              // gesetzliche Frist (VatReturn.dueDate)
    dueDateEffective: Date;     // nach § 108 Abs. 3 AO – Basis des Vorlaufs
    regionHint: string | null;
    existingReturn: VatReturn | null;
}

export interface VatDeadlineTasksRequest {
    landlordID: string;
    landlordName: string;
    customerID: string | null;
    uid: string;
    periods: VatDeadlinePeriod[];
    leadDays: VatDeadlineLeadDays;
    recipients: VatDeadlineRecipient[];
    now: Date;
}

export interface VatDeadlineTasksResult {
    created: Array<{ returnID: string; taskID: string; reminderID: string }>;
    skipped: Array<{ returnID: string; reason: 'EXISTS' }>;
}

/**
 * Fristen-Aufgaben (Spec 9): je Zeitraum eine private Aufgabe unter users/{uid}/tasks
 * (setParentPath → set(merge) → finally resetPath), eine Erinnerung (BOTH, ONE_TIME, sourceFeature VAT)
 * und die IDs in `fristen.taskID/reminderID` per update() mit dotted keys. Fehlt das VatReturn-Dokument,
 * entsteht es hier als ENTWURF (A9). Komponenten-Provider (NahausRemindersService ist nicht root).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable()
export class VatDeadlineTasksService {

    constructor(
        private tasksService: TasksService,
        private remindersService: NahausRemindersService,
        private vatReturnsService: VatReturnsService,
    ) {
    }

    async create(request: VatDeadlineTasksRequest): Promise<VatDeadlineTasksResult> {
        const result: VatDeadlineTasksResult = {created: [], skipped: []};
        for (const period of request.periods) {
            const vatReturn = await this.ensureReturn(request, period);
            if (vatReturn.fristen?.taskID) {
                result.skipped.push({returnID: vatReturn.id, reason: 'EXISTS'});
                continue;
            }
            const taskID = this.tasksService.createID();
            const reminderID = this.remindersService.createID();
            const params = {
                taskID, uid: request.uid, customerID: request.customerID, landlordID: request.landlordID, landlordName: request.landlordName,
                returnID: vatReturn.id, year: period.year, periodKey: period.periodKey, dueDateEffective: period.dueDateEffective,
                leadDays: request.leadDays, regionHint: period.regionHint, now: request.now,
            };

            this.tasksService.setParentPath(request.uid);
            try {
                await this.tasksService.doc(taskID).set(buildDeadlineTaskPayload(params), {merge: true});
            } finally {
                this.tasksService.resetPath();
            }

            await this.remindersService.create(buildDeadlineReminderPayload({...params, reminderID, recipients: request.recipients}));
            await this.vatReturnsService.updateFields(vatReturn.id, {'fristen.taskID': taskID, 'fristen.reminderID': reminderID});
            result.created.push({returnID: vatReturn.id, taskID, reminderID});
        }
        return result;
    }

    /** Übergebenes Dokument → Server-Stand → sonst ENTWURF anlegen (Spec 3.4: Dokument entsteht beim ersten Schreibvorgang). */
    private async ensureReturn(request: VatDeadlineTasksRequest, period: VatDeadlinePeriod): Promise<VatReturn> {
        if (period.existingReturn) {
            return period.existingReturn;
        }
        const id = buildVatReturnID(request.landlordID, period.year, period.periodKey, 1);
        const snapshot = await firstValueFrom(this.vatReturnsService.doc(id).get({source: 'server'}));
        if (snapshot.exists) {
            return normalizeVatReturnDates({...(snapshot.data() as VatReturn), id});
        }
        const range = rangeOfPeriod(period.year, period.periodKey);
        const draft = buildDraftVatReturn({
            customerID: request.customerID, landlordID: request.landlordID, year: period.year, periodKey: period.periodKey,
            startDate: range.start, endDate: range.end, dueDate: period.dueDate, uid: request.uid, now: request.now, engineVersion: ENGINE_VERSION,
        });
        await this.vatReturnsService.create(draft);
        return draft;
    }
}
